import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import "./App.css";
import api from './api';
import MainContext from "./common/MainContext";
import "./common/i18n";
import ErrorBanner from './components/ErrorBanner';
import ErrorBoundary from './components/ErrorBoundary';
import NotificationBanner from './components/NotificationBanner';
import ProtectedRoute from './components/ProtectedRoute';
import LoginDialog from './components/dialogs/LoginDialog';
import NotificationPermissionDialog from './components/dialogs/NotificationPermissionDialog';
import SideNav from './components/layouts/SideNav';
import ThemeProvider from "./components/layouts/ThemeProvider";
import { ModulePages } from './enums';
import app, { auth } from "./firebase";
import ActionHandler from './pages/ActionHandler';
import ChangePassword from './pages/ChangePassword';
import Courses from './pages/Courses';
import Dashboard from "./pages/Dashboard";
import Exercise from './pages/Exercise';
import Exercises from './pages/Exercises';
import Lessons from './pages/Lessons';
import LiveLesson from './pages/LiveLesson';
import Login from './pages/Login';
import Materials from './pages/Materials';
import Chats from './pages/Chats';
import ModuleDrawer from './pages/ModuleDrawer';
import Notifications from "./pages/Notifications";
import PageNotFound from './pages/PageNotFound';
import Profile from './pages/Profile';
import TestResult from './pages/TestResult';
import Tests from './pages/Tests';
import "./themes.css";
import { mapServerError } from './common/Errors';
import VersionWatcher from './components/VersionWatcher';
import { WebSocketProvider } from './common/WebSocketContext';

function App() {
  const [user, setUser] = useState(null)
  const [dropdown, setDropdown] = useState(null)
  const [loginDialog, setLoginDialog] = useState(null)
  const [notificationPermissions, setNotificationPermissions] = useState(false)
  const [notificationBanner, setNotificationBanner] = useState(null)
  const [notifications, setNotifications] = useState(null)
  const [unreadMessages, _setUnreadMessages] = useState(0)
  const [whiteboard, setWhiteboard] = useState({ show: false, persistenceKey: null, pdf: null })
  const [error, setError] = useState(null)
  const [sidenav, setSidenav] = useState({ open: true, visible: true })
  const [authReady, setAuthReady] = useState(false);
  const [fcmToken, setFcmToken] = useState(null)
  const [appVersion, setAppVersion] = useState(null)
  const [showVersionWatcher, setShowVersionWatcher] = useState(false)

  const setUnreadMessages = data => {
    unreadMessageRef.current = data
    _setUnreadMessages(data)
  }
  const scrollRef = useRef(null)

  const context = {
    user: user,
    setUser: setUser,
    isLogging: null,
    dropdown: dropdown,
    setDropdown: setDropdown,
    loginDialog: loginDialog,
    setLoginDialog: setLoginDialog,
    notificationPermissions: notificationPermissions,
    setNotificationPermissions: setNotificationPermissions,
    notificationBanner: notificationBanner,
    setNotificationBanner: setNotificationBanner,
    notifications: notifications,
    setNotifications: setNotifications,
    unreadMessages: unreadMessages,
    setUnreadMessages: setUnreadMessages,
    whiteboard: whiteboard,
    setWhiteboard: setWhiteboard,
    scrollRef,
    error: error,
    setError: setError,
    sidenav: sidenav,
    setSidenav: setSidenav,
    fcmToken: fcmToken,
    setFcmToken: setFcmToken,
    appVersion: appVersion,
    setAppVersion: setAppVersion
  }


  const unreadMessageRef = useRef(context.unreadMessages)
  const getUserInFlight = useRef(false)

  const getNotifications = useCallback(async () => {
    try {
      let notifications = await api.get("/teacher/notifications?page=1&per=4")
      setNotifications(notifications)
    }
    catch (e) {
      console.error(e)
    }
  }, [])

  //Ottiene il token FCM e lo invia al backend.
  const getNotificationsToken = useCallback(async () => {
    try {
      let supported = await isSupported()
      if (supported) {
        await navigator.serviceWorker.register('/firebase-messaging-sw.js')
        let messaging = getMessaging(app)
        if (messaging) {
          if (Notification.permission === "granted") {
            try {
              let token = await getToken(messaging, { vapidKey: process.env.REACT_APP_FIREBASE_VAPID })
              setFcmToken(token)
              await api.post("/teacher/tokens", { token: token })
            }
            catch (e) {
              console.error(e)
            }
          } else {
            console.warn("Notifications Permission Denied")
          }
        }
      }
    } catch (e) {
      console.error(e)
    }

  }, [])

  const handleError = useCallback((event) => {
    const error = mapServerError(event.detail);
    setError({ title: error.message, message: error.detail });
  }, [])

  useEffect(() => {

    if (window.location.hostname === "teachers-beta.startingfinance.com") {
      const metaTag = document.createElement("meta");
      metaTag.name = "robots";
      metaTag.content = "noindex, nofollow";
      document.head.appendChild(metaTag);
    }

    //Gestisce le notifiche push ricevute con app aperta.
    isSupported().then((supported) => {
      if (supported) {
        let messaging = getMessaging(app)
        if (messaging) {
          onMessage(messaging, (payload) => {
            setNotificationBanner({ title: payload.notification.title, body: payload.notification.body, image: payload.notification.image })
          })
        }
      }
    })

    document.addEventListener('api-error', handleError);

    // Defer non-critical watchers to idle
    const idle = () => setShowVersionWatcher(true);
    'requestIdleCallback' in window ? window.requestIdleCallback(idle) : setTimeout(idle, 1200);

    return () => {
      document.removeEventListener('api-error', handleError)
    }

  }, [handleError]);

  // Inizializza lo stato di auth UNA sola volta
  useEffect(() => {
    let cancelled = false
    auth.authStateReady().then(() => {
      if (!cancelled) setAuthReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const checkAuthUser = useCallback(async (user) => {
    //Se l'utente è loggato su firebase
    user = user ?? auth.currentUser

    if (user) {
      // //Ottiene le info dell'utente dal backend con retry.
      const getUser = async () => {
        if (getUserInFlight.current) {
          return
        }
        getUserInFlight.current = true
        const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))
        const maxRetries = 5
        const delayMs = 2000

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            let u = await api.get("/teacher/")
            if (u) {
              setUser(u)
              setLoginDialog(false)
              getUserInFlight.current = false
              return
            }
          }
          catch (e) {
            console.error(`getUser attempt ${attempt} failed`, e)
            if (attempt < maxRetries) {
              await sleep(delayMs)
              continue
            }
            // Dopo l'ultimo tentativo fallito esegui il signOut
            await auth.signOut()
            setUser(null)
          }
        }
        getUserInFlight.current = false
      }

      getUser()
    }
  }, [])

  // Quando auth è pronto, sottoscrivi i cambiamenti e affida a onAuthStateChanged la chiamata iniziale
  useEffect(() => {
    if (!authReady) return

    const unsubscribe = auth.onAuthStateChanged((user) => {
      checkAuthUser(user)
    })

    return () => unsubscribe()
  }, [authReady, checkAuthUser])

  /**
   * Quando cambia l'utente nel context
   */
  // Gestione side-effect dopo l'impostazione dell'utente
  useEffect(() => {
    if (user) {
      isSupported().then((supported) => {
        if (supported) {
          if (Notification.permission === 'default' && localStorage.getItem('notifications') !== 'denied') {
            setNotificationPermissions(true)
          }
          if (Notification.permission === 'granted') {
            localStorage.setItem('notifications', 'granted')
            if (!fcmToken) {
              getNotificationsToken()
            }
          }
        }
      })

      // //Elimina l'utente anonimo, ottieni il carrello, le notifiche e inizializza la connessione websocket per i messaggi
      if (notifications === null) {
        getNotifications()
      }

    }
  }, [user, fcmToken, notifications, getNotifications, getNotificationsToken])


  return (
    <Router>
      <MainContext.Provider value={context}>
        <ThemeProvider theme={'starting-finance'}>
          {context &&
            <WebSocketProvider user={context.user}>
              <ErrorBoundary>
                <div className="container">
                  <SideNav onChange={(open) => {
                    context.setSidenav((prev) => {
                      prev.open = open
                      return { ...prev }
                    })
                  }} show={context.sidenav.visible}></SideNav>

                  {showVersionWatcher && context.user &&(
                    <VersionWatcher />
                  )}

                  <LoginDialog open={context.loginDialog} onClose={() => { context.setLoginDialog(false) }} />
                  <NotificationPermissionDialog open={context.notificationPermissions} onClose={() => {
                    context.setNotificationPermissions(false)
                    getNotificationsToken()
                  }} />
                  <NotificationBanner notification={context.notificationBanner} />
                  <ErrorBanner error={context.error} />

                  <div id="app-content" className='content'>
                    <div ref={scrollRef} />
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route path="*" element={<PageNotFound />} />
                      <Route path="/actions" element={<ActionHandler />} />

                      <Route path="/" element={<ProtectedRoute component={<><Dashboard /></>} />} />
                      <Route path="/courses" element={<ProtectedRoute component={<><Courses /></>} />} />
                      <Route path="/courses/:moduleId" element={<ProtectedRoute component={<><ModuleDrawer /></>} />} />
                      <Route path="/courses/:moduleId/students" element={<ProtectedRoute component={<><ModuleDrawer defaultSelectedTab={ModulePages.Students} /></>} />} />
                      <Route path="/courses/:moduleId/lessons" element={<ProtectedRoute component={<><ModuleDrawer defaultSelectedTab={ModulePages.Lessons} /></>} />} />
                      <Route path="/courses/:moduleId/materials" element={<ProtectedRoute component={<><ModuleDrawer defaultSelectedTab={ModulePages.Materials} /></>} />} />
                      <Route path="/courses/:moduleId/exercises" element={<ProtectedRoute component={<><ModuleDrawer defaultSelectedTab={ModulePages.Exercises} /></>} />} />
                      <Route path="/courses/:moduleId/tests" element={<ProtectedRoute component={<><ModuleDrawer defaultSelectedTab={ModulePages.Tests} /></>} />} />
                      <Route path="/tests" element={<ProtectedRoute component={<><Tests /></>} />} />
                      <Route path="/tests/:testId" element={<ProtectedRoute component={<><TestResult /></>} />} />
                      <Route path="/lessons" element={<ProtectedRoute component={<><Lessons /></>} />} />
                      <Route path="/lessons/:roomName" element={<ProtectedRoute component={<><LiveLesson /></>} />} />
                      <Route path="/materials" element={<ProtectedRoute component={<><Materials /></>} />} />
                      <Route path="/exercises/" element={<ProtectedRoute component={<><Exercises /></>} />} />
                      <Route path="/exercises/:exerciseId" element={<ProtectedRoute component={<><Exercise /></>} />} />
                      <Route path="/notifications" element={<ProtectedRoute component={<><Notifications /></>} />} />
                      <Route path="/messages/" element={<ProtectedRoute component={<><Chats /></>} />} />
                      <Route path="/profile" element={<ProtectedRoute component={<><Profile /></>} />} />
                      <Route path="/changePassword" element={<ProtectedRoute component={<><ChangePassword /></>} />} />
                    </Routes>
                  </div>
                </div>
              </ErrorBoundary>
            </WebSocketProvider>
          }
        </ThemeProvider>
      </MainContext.Provider>
    </Router>
  );
}

export default App;
